/**
 * CalendarPage - Страница календаря активностей
 *
 * Отображает встречи, звонки и мастер-классы в календарном виде.
 * Поддерживает виды: месяц, неделя, день.
 * Фильтры по типу события.
 * Ролевая модель: SALES - свои, MANAGER/ADMIN - все.
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  Box,
  Title,
  Group,
  SegmentedControl,
  Text,
  Stack,
  Loader,
  Center,
  Paper,
  Alert,
  Button,
  Modal,
  Checkbox,
} from '@mantine/core';
import { MonthPicker } from '@mantine/dates';
import {
  IconCalendar,
  IconPhone,
  IconUsers,
  IconSchool,
  IconFilter,
  IconAlertCircle,
  IconRefresh,
} from '@tabler/icons-react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import listPlugin from '@fullcalendar/list';
import interactionPlugin from '@fullcalendar/interaction';
import ruLocale from '@fullcalendar/core/locales/ru';
import tokens from '../theme/designTokens';
import EventDetailsModal from '../components/Calendar/EventDetailsModal';
import '../components/Calendar/calendar-styles.css';

// Типы событий для фильтра
const EVENT_TYPES = [
  { value: 'all', label: 'Все', icon: IconFilter },
  { value: 'meeting', label: 'Встречи', icon: IconUsers },
  { value: 'call', label: 'Звонки', icon: IconPhone },
  { value: 'master_class', label: 'МК', icon: IconSchool },
];

const CalendarPage = () => {
  const calendarRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [eventFilter, setEventFilter] = useState('all');
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [detailsModalOpened, setDetailsModalOpened] = useState(false);
  const [summary, setSummary] = useState({ total: 0, meetings: 0, calls: 0, master_classes: 0 });
  const [currentDate, setCurrentDate] = useState(new Date());
  const [monthPickerOpened, setMonthPickerOpened] = useState(false);
  const [showCancelled, setShowCancelled] = useState(true);
  const [showCompleted, setShowCompleted] = useState(true);

  // Форматирование даты для заголовка
  const formatCurrentPeriod = useCallback(() => {
    if (!calendarRef.current) {
      return currentDate.toLocaleString('ru-RU', { month: 'long', year: 'numeric' });
    }
    const calendarApi = calendarRef.current.getApi();
    const view = calendarApi.view;
    const start = view.currentStart;
    const end = view.currentEnd;

    // Для вида месяца показываем только месяц и год
    if (view.type === 'dayGridMonth') {
      return start.toLocaleString('ru-RU', { month: 'long', year: 'numeric' });
    }

    // Для недели/дня показываем диапазон
    const startMonth = start.toLocaleString('ru-RU', { month: 'short', day: 'numeric' });
    const endDate = new Date(end);
    endDate.setDate(endDate.getDate() - 1); // end - это следующий день после окончания
    const endMonth = endDate.toLocaleString('ru-RU', { month: 'short', day: 'numeric', year: 'numeric' });

    return `${startMonth} – ${endMonth}`;
  }, [currentDate]);

  // Обновление текущей даты при изменении вида календаря
  const handleDatesSet = (dateInfo) => {
    setCurrentDate(dateInfo.start);
  };

  // Переход к выбранному месяцу
  const handleMonthSelect = (date) => {
    if (calendarRef.current && date) {
      const calendarApi = calendarRef.current.getApi();
      calendarApi.gotoDate(date);
      setCurrentDate(date);
    }
    setMonthPickerOpened(false);
  };

  // Обновление текста кнопки при изменении даты
  useEffect(() => {
    const updateButtonText = () => {
      const btn = document.querySelector('.fc-customTitle-button');
      if (btn) {
        btn.innerHTML = `<span style="display: flex; align-items: center; gap: 6px;">${formatCurrentPeriod()} <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m6 9 6 6 6-6"/></svg></span>`;
      }
    };
    // Небольшая задержка для уверенности, что DOM обновился
    const timer = setTimeout(updateButtonText, 50);
    return () => clearTimeout(timer);
  }, [formatCurrentPeriod]);

  // Загрузка событий при изменении диапазона дат
  const fetchEvents = useCallback(async (fetchInfo, successCallback, failureCallback) => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        start: fetchInfo.startStr,
        end: fetchInfo.endStr,
      });

      // Добавляем фильтр по типу (если не "all")
      if (eventFilter !== 'all') {
        params.append('event_types', eventFilter);
      }

      const response = await fetch(`/api/calendar/events?${params}`, {
        credentials: 'include',
      });

      if (!response.ok) {
        if (response.status === 403) {
          throw new Error('У вас нет доступа к календарю');
        }
        throw new Error('Не удалось загрузить события');
      }

      const data = await response.json();
      setSummary(data.summary);

      // Преобразуем в формат FullCalendar
      let calendarEvents = data.events.map((event) => ({
        id: event.id,
        title: event.title,
        start: event.start,
        end: event.end,
        backgroundColor: event.color,
        borderColor: event.color,
        extendedProps: {
          type: event.type,
          status: event.status,
          organization: event.organization,
          details: event.details,
        },
      }));

      // Фильтрация по статусам
      calendarEvents = calendarEvents.filter((event) => {
        const status = event.extendedProps.status;
        // Скрываем отменённые, если чекбокс выключен
        if (!showCancelled && status === 'cancelled') {
          return false;
        }
        // Скрываем завершённые, если чекбокс выключен
        if (!showCompleted && status === 'completed') {
          return false;
        }
        return true;
      });

      successCallback(calendarEvents);
    } catch (err) {
      console.error('Failed to fetch calendar events:', err);
      setError(err.message);
      failureCallback(err);
    } finally {
      setLoading(false);
    }
  }, [eventFilter, showCancelled, showCompleted]);

  // Обработчик клика по событию
  const handleEventClick = (clickInfo) => {
    setSelectedEvent({
      id: clickInfo.event.id,
      title: clickInfo.event.title,
      start: clickInfo.event.start,
      end: clickInfo.event.end,
      ...clickInfo.event.extendedProps,
    });
    setDetailsModalOpened(true);
  };

  // Обновление событий при изменении фильтра типов
  const handleFilterChange = (value) => {
    setEventFilter(value);
    // Принудительно обновляем данные
    if (calendarRef.current) {
      const calendarApi = calendarRef.current.getApi();
      calendarApi.refetchEvents();
    }
  };

  // Обновление событий при изменении фильтров статусов
  useEffect(() => {
    if (calendarRef.current) {
      const calendarApi = calendarRef.current.getApi();
      calendarApi.refetchEvents();
    }
  }, [showCancelled, showCompleted]);

  // Ручное обновление
  const handleRefresh = () => {
    if (calendarRef.current) {
      const calendarApi = calendarRef.current.getApi();
      calendarApi.refetchEvents();
    }
  };

  return (
    <Box px="md" py="md">
      <Stack gap="md">
        {/* Заголовок и фильтры */}
        <Group justify="space-between" align="flex-end" wrap="wrap" gap="md">
          <Box>
            <Title order={2} style={{ color: tokens.colors.brand.primary }}>
              <Group gap="xs">
                <IconCalendar size={28} />
                Календарь
              </Group>
            </Title>
            <Text c="dimmed" size="sm">
              Встречи: {summary.meetings} | Звонки: {summary.calls} | МК: {summary.master_classes} | Всего: {summary.total}
            </Text>
          </Box>

          <Group gap="sm" wrap="wrap">
            {/* Кнопка обновления */}
            <Button
              variant="light"
              leftSection={<IconRefresh size={16} />}
              onClick={handleRefresh}
              loading={loading}
              size="sm"
            >
              Обновить
            </Button>

            {/* Фильтр по типу события */}
            <SegmentedControl
              value={eventFilter}
              onChange={handleFilterChange}
              size="sm"
              radius="xl"
              data={EVENT_TYPES.map((t) => ({
                value: t.value,
                label: (
                  <Group gap={4} wrap="nowrap">
                    <t.icon size={16} />
                    <span>{t.label}</span>
                  </Group>
                ),
              }))}
            />
          </Group>
        </Group>

        {/* Фильтр по статусам */}
        <Group justify="flex-end" gap="lg">
          <Checkbox
            label="Отменённые"
            checked={showCancelled}
            onChange={(e) => setShowCancelled(e.currentTarget.checked)}
            size="sm"
            radius="xl"
          />
          <Checkbox
            label="Завершённые"
            checked={showCompleted}
            onChange={(e) => setShowCompleted(e.currentTarget.checked)}
            size="sm"
            radius="xl"
          />
        </Group>

        {/* Ошибка */}
        {error && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            title="Ошибка"
            color="red"
            variant="light"
            withCloseButton
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}

        {/* Календарь */}
        <Paper
          radius="xl"
          p="md"
          style={{
            border: `1px solid ${tokens.colors.neutral[3]}`,
            position: 'relative',
            minHeight: '600px',
            overflow: 'hidden',
          }}
        >
          {loading && (
            <Center
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(255,255,255,0.8)',
                zIndex: 10,
              }}
            >
              <Loader size="lg" />
            </Center>
          )}

          <FullCalendar
            ref={calendarRef}
            plugins={[dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin]}
            initialView="dayGridMonth"
            locale={ruLocale}
            headerToolbar={{
              left: 'prev,next today',
              center: 'customTitle',
              right: 'dayGridMonth,timeGridWeek,timeGridDay,listWeek',
            }}
            customButtons={{
              customTitle: {
                text: '',
                click: () => setMonthPickerOpened(true),
              },
            }}
            buttonText={{
              today: 'Сегодня',
              month: 'Месяц',
              week: 'Неделя',
              day: 'День',
              list: 'Список',
            }}
            events={fetchEvents}
            eventClick={handleEventClick}
            datesSet={handleDatesSet}
            height="auto"
            dayMaxEvents={3}
            eventDisplay="block"
            eventTimeFormat={{
              hour: '2-digit',
              minute: '2-digit',
              hour12: false,
            }}
            slotMinTime="07:00:00"
            slotMaxTime="21:00:00"
            allDaySlot={false}
            nowIndicator={true}
            weekends={true}
            firstDay={1}
            // Стили для событий
            eventDidMount={(info) => {
              // Добавляем стили в зависимости от статуса
              const status = info.event.extendedProps.status;
              if (status === 'completed') {
                info.el.style.opacity = '0.7';
              } else if (status === 'cancelled') {
                info.el.style.opacity = '0.6';
                info.el.classList.add('fc-event-cancelled');
              } else if (status === 'missed') {
                info.el.style.border = '2px solid #C23934';
              }
            }}
          />

        </Paper>
      </Stack>

      {/* Модальное окно деталей события */}
      <EventDetailsModal
        opened={detailsModalOpened}
        onClose={() => setDetailsModalOpened(false)}
        event={selectedEvent}
      />

      {/* Модальное окно выбора месяца */}
      <Modal
        opened={monthPickerOpened}
        onClose={() => setMonthPickerOpened(false)}
        title="Выберите месяц"
        centered
        size="auto"
      >
        <MonthPicker
          value={currentDate}
          onChange={handleMonthSelect}
          locale="ru"
        />
      </Modal>
    </Box>
  );
};

export default CalendarPage;
